export interface DbSubmission {
  id: string;
  customer_email: string;
  customer_name: string | null;
  tier: string;
  industry: string | null;
  phone: string | null;
  description: string | null;
  comment: string | null;
  status: 'pending' | 'processing' | 'queued' | 'completed' | 'failed';
  investors_contacted: number;
  target_count: number;
  use_ai_search: boolean;
  pdf_blob_url: string;
  file_metadata: Record<string, unknown> | null;
  stripe_session_id: string | null;
  amount_paid: number | null;
  subscription_id: number | null;
  is_monthly_auto_send: boolean;
  processing_stopped: boolean;
  summary_email_sent_at: Date | null;
  created_at: Date;
  processed_at: Date | null;
  /**
   * Non-null if and only if `status` is 'completed'. Every write that changes
   * status must say what happens to this: a run that is queued, processing,
   * pending or failed has not completed, and the admin timeline renders any
   * non-null value as a "Completed" entry with a date. The pairing is checked
   * in app/api/cron/monthly-automation/__tests__/status-transitions.test.ts.
   */
  completed_at: Date | null;
  last_reminder_sent_at: Date | null;
  metadata: Record<string, unknown> | null;
}

export interface Pricing {
    amount: number;
    currency: string;
    description: string;
}

// File types
export interface FileWithPreview extends File {
  preview: string;
}

// Form submission types
export interface FormData {
  email: string;
  comment?: string;
  files: FileWithPreview[];
}

// Stripe types
export interface StripeSessionData {
  id: string;
  amount: number;
  customer: string;
  customerEmail: string;
}

// Email types
export interface EmailData {
  to: string;
  from: string;
  subject: string;
  html: string;
  attachments?: EmailAttachment[];
}

export interface EmailAttachment {
  filename: string;
  content: Buffer;
  contentType: string;
}

// API response types
export interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Testimonial type
export interface Testimonial {
  id: number;
  name: string;
  company: string;
  quote: string;
  imageSrc?: string;
}

// Navigation item type
export interface NavItem {
  label: string;
  href: string;
}

// Footer link type
export interface FooterLink {
  label: string;
  href: string;
  isExternal?: boolean;
}

// Cookie consent type
export interface CookieConsentState {
  accepted: boolean;
  declined: boolean;
}

// Pricing tier types
export type PricingTierId = 'starter' | 'professional' | 'scale';
export type BillingType = 'monthly' | 'yearly';

export interface PricingTier {
  id: PricingTierId;
  name: string;
  priceMonthly: number; // in cents
  priceYearly: number; // in cents (per year, 20% discount)
  vcCount: number;
  features: string[];
  highlighted?: boolean;
}

// Extended form data with additional fields
export interface ExtendedFormData extends FormData {
  name?: string;
  industry?: string;
  phone?: string;
  description?: string;
  tier?: PricingTierId;
  billingType?: BillingType;
  couponCode?: string;
  selectedUpsells?: string[];
}

// Industry options
export const INDUSTRY_OPTIONS = [
  'fintech',
  'healthtech',
  'saas',
  'ai_ml',
  'marketplace',
  'ecommerce',
  'edtech',
  'cleantech',
  'proptech',
  'gaming',
  'other'
] as const;

export type IndustryType = typeof INDUSTRY_OPTIONS[number];

// Upsell types
export interface Upsell {
  id: string;
  name: string;
  price: number; // in cents
  enabled: boolean;
  availableTiers: PricingTierId[];
}

// Pricing configuration
//
// Tier differentiation is monthly contact volume (10 / 50 / 150).
// Nothing else differs between tiers in code: AI matching,
// email personalization and industry matching are applied identically to every
// tier, and all tiers reach effectively the same investor pool.
//
// Every key in `features` must correspond to something the code actually gates
// or actually does. This list drifted before — it advertised tier gates
// (template quality, industry breadth, premium investors, follow-ups, priority
// matching) that had no implementation. Do not add a key here before the
// behaviour behind it exists.
export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    priceMonthly: 1900, // $19
    priceYearly: 18200, // $182/year (20% off monthly)
    vcCount: 10,
    features: [
      'automatedSending10',
      'aiFittedCommunication',
      'multiIndustry',
      'aiMatching',
      'industryIdentification',
      'vcFromAllSources',
      'personalProjectWebsite',
      'interestTracking',
      'emailNotifications',
      'realtimeTracking',
      'outreachLog',
      'userDashboard',
      'submissionAnalytics',
      'twentyFourHourDelivery',
      'monthlyReports'
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    priceMonthly: 4900, // $49
    priceYearly: 47000, // $470/year (20% off monthly)
    vcCount: 50,
    features: [
      'automatedSending50',
      'aiFittedCommunication',
      'multiIndustry',
      'aiMatching',
      'industryIdentification',
      'vcFromAllSources',
      'personalProjectWebsite',
      'interestTracking',
      'emailNotifications',
      'realtimeTracking',
      'outreachLog',
      'userDashboard',
      'submissionAnalytics',
      'twentyFourHourDelivery',
      'monthlyReports'
    ],
    highlighted: true
  },
  {
    id: 'scale',
    name: 'Scale',
    priceMonthly: 9900, // $99
    priceYearly: 95000, // $950/year (20% off monthly)
    vcCount: 150,
    features: [
      'automatedSending150',
      'aiFittedCommunication',
      'multiIndustry',
      'aiMatching',
      'industryIdentification',
      'vcFromAllSources',
      'personalProjectWebsite',
      'interestTracking',
      'emailNotifications',
      'realtimeTracking',
      'outreachLog',
      'userDashboard',
      'submissionAnalytics',
      'twentyFourHourDelivery',
      'monthlyReports'
    ]
  }
];

// Admin Processing Log types
export interface LogEntry {
  id: string;
  timestamp: Date;
  type: 'info' | 'success' | 'error';
  submissionId: string;
  customerEmail?: string;
  message: string;
}

export interface ProcessingSummary {
  total: number;
  successful: number;
  failed: number;
}
